/**
 * Avakhada details — the classical birth-identity table (Varna, Vashya, Yoni,
 * Gana, Nadi, …) shown at the top of every printed Kundli.
 *
 * VedAstro exposes these only as two-chart kuta comparisons, not per person,
 * so the single-chart values are derived here from API facts (Moon nakshatra
 * and Moon sign) via the standard Jyotish lookup tables — the same fixed
 * tables every panchang uses. Nakshatra keys follow VedAstro's South-Indian
 * spellings (Swathi, Chitta, Pushyami…) as returned by the API.
 */

/* Sign → Sanskrit name; Varna and Vashya classification by Moon sign. */
export const SIGN_TABLE = {
  Aries: { sa: 'Mesha', varna: 'Kshatriya', vashya: 'Chatushpada (Quadruped)' },
  Taurus: { sa: 'Vrishabha', varna: 'Vaishya', vashya: 'Chatushpada (Quadruped)' },
  Gemini: { sa: 'Mithuna', varna: 'Shudra', vashya: 'Manav (Human)' },
  Cancer: { sa: 'Karka', varna: 'Brahmin', vashya: 'Jalchar (Aquatic)' },
  Leo: { sa: 'Simha', varna: 'Kshatriya', vashya: 'Vanchar (Wild)' },
  Virgo: { sa: 'Kanya', varna: 'Vaishya', vashya: 'Manav (Human)' },
  Libra: { sa: 'Tula', varna: 'Shudra', vashya: 'Manav (Human)' },
  Scorpio: { sa: 'Vrischika', varna: 'Brahmin', vashya: 'Keet (Insect)' },
  Sagittarius: { sa: 'Dhanu', varna: 'Kshatriya', vashya: 'Manav (Human)' },
  Capricorn: { sa: 'Makara', varna: 'Vaishya', vashya: 'Jalchar (Aquatic)' },
  Aquarius: { sa: 'Kumbha', varna: 'Shudra', vashya: 'Manav (Human)' },
  Pisces: { sa: 'Meena', varna: 'Brahmin', vashya: 'Jalchar (Aquatic)' },
}

/*
 * The 27 nakshatras keyed by VedAstro's spelling: common display name, Yoni
 * (animal symbol), Gana (temperament) and Nadi (constitution).
 */
export const NAKSHATRA_TABLE = {
  aswini: { display: 'Ashwini', yoni: 'Ashwa (Horse)', gana: 'Deva', nadi: 'Adi' },
  bharani: { display: 'Bharani', yoni: 'Gaja (Elephant)', gana: 'Manushya', nadi: 'Madhya' },
  krithika: { display: 'Krittika', yoni: 'Mesha (Sheep)', gana: 'Rakshasa', nadi: 'Antya' },
  rohini: { display: 'Rohini', yoni: 'Sarpa (Serpent)', gana: 'Manushya', nadi: 'Antya' },
  mrigasira: { display: 'Mrigashira', yoni: 'Sarpa (Serpent)', gana: 'Deva', nadi: 'Madhya' },
  aridra: { display: 'Ardra', yoni: 'Shwan (Dog)', gana: 'Manushya', nadi: 'Adi' },
  punarvasu: { display: 'Punarvasu', yoni: 'Marjara (Cat)', gana: 'Deva', nadi: 'Adi' },
  pushyami: { display: 'Pushya', yoni: 'Mesha (Sheep)', gana: 'Deva', nadi: 'Madhya' },
  aslesha: { display: 'Ashlesha', yoni: 'Marjara (Cat)', gana: 'Rakshasa', nadi: 'Antya' },
  makha: { display: 'Magha', yoni: 'Mushaka (Rat)', gana: 'Rakshasa', nadi: 'Antya' },
  pubba: { display: 'Purva Phalguni', yoni: 'Mushaka (Rat)', gana: 'Manushya', nadi: 'Madhya' },
  uttara: { display: 'Uttara Phalguni', yoni: 'Gau (Cow)', gana: 'Manushya', nadi: 'Adi' },
  hasta: { display: 'Hasta', yoni: 'Mahisha (Buffalo)', gana: 'Deva', nadi: 'Adi' },
  chitta: { display: 'Chitra', yoni: 'Vyaghra (Tiger)', gana: 'Rakshasa', nadi: 'Madhya' },
  swathi: { display: 'Swati', yoni: 'Mahisha (Buffalo)', gana: 'Deva', nadi: 'Antya' },
  vishhaka: { display: 'Vishakha', yoni: 'Vyaghra (Tiger)', gana: 'Rakshasa', nadi: 'Antya' },
  anuradha: { display: 'Anuradha', yoni: 'Mriga (Deer)', gana: 'Deva', nadi: 'Madhya' },
  jyesta: { display: 'Jyeshtha', yoni: 'Mriga (Deer)', gana: 'Rakshasa', nadi: 'Adi' },
  moola: { display: 'Mula', yoni: 'Shwan (Dog)', gana: 'Rakshasa', nadi: 'Adi' },
  poorvashada: { display: 'Purva Ashadha', yoni: 'Vanara (Monkey)', gana: 'Manushya', nadi: 'Madhya' },
  uttarashada: { display: 'Uttara Ashadha', yoni: 'Nakula (Mongoose)', gana: 'Manushya', nadi: 'Antya' },
  sravana: { display: 'Shravana', yoni: 'Vanara (Monkey)', gana: 'Deva', nadi: 'Antya' },
  dhanishta: { display: 'Dhanishta', yoni: 'Simha (Lion)', gana: 'Rakshasa', nadi: 'Madhya' },
  satabhisha: { display: 'Shatabhisha', yoni: 'Ashwa (Horse)', gana: 'Rakshasa', nadi: 'Adi' },
  poorvabhadra: { display: 'Purva Bhadrapada', yoni: 'Simha (Lion)', gana: 'Manushya', nadi: 'Adi' },
  uttarabhadra: { display: 'Uttara Bhadrapada', yoni: 'Gau (Cow)', gana: 'Manushya', nadi: 'Madhya' },
  revathi: { display: 'Revati', yoni: 'Gaja (Elephant)', gana: 'Deva', nadi: 'Antya' },
}

/** Look up a nakshatra tolerantly by VedAstro name (falls back to the raw name). */
export function nakshatraInfo(name) {
  const key = String(name || '').toLowerCase().trim()
  return NAKSHATRA_TABLE[key] || { display: name || '—', yoni: '—', gana: '—', nadi: '—' }
}

export function signSanskrit(en) {
  return SIGN_TABLE[en]?.sa || en || '—'
}

/**
 * Assemble the Avakhada table from already-fetched API data:
 * `planets` from getAllPlanetData, `houses` from getAllHouseData and
 * `panchang` from getPanchang at the exact birth time.
 */
export function buildAvakhada({ planets = [], houses = [], panchang = null }) {
  const moon = planets.find((p) => p.name === 'Moon') || {}
  const sun = planets.find((p) => p.name === 'Sun') || {}
  const lagnaSign = houses.find((h) => h.house === 1)?.sign || null
  const nak = nakshatraInfo(moon.nakshatra)
  const signRow = SIGN_TABLE[moon.sign] || {}
  return {
    moonSign: moon.sign || '—',
    moonSignSa: signSanskrit(moon.sign),
    moonSignLord: moon.signLord || '—',
    sunSign: sun.sign || '—',
    sunSignSa: signSanskrit(sun.sign),
    lagna: lagnaSign || '—',
    lagnaSa: signSanskrit(lagnaSign),
    nakshatra: nak.display,
    pada: moon.pada || '—',
    nakshatraLord: moon.nakshatraLord || '—',
    varna: signRow.varna || '—',
    vashya: signRow.vashya || '—',
    yoni: nak.yoni,
    gana: nak.gana,
    nadi: nak.nadi,
    tithi: panchang?.tithi?.Name || '—',
    paksha: panchang?.tithi?.Paksha || '',
    yoga: panchang?.yoga?.Name || panchang?.yoga || '—',
    karana: panchang?.karana || '—',
  }
}
